import { apiClient } from './client';

/**
 * Client for `/api/invitations` + `/api/employees/import`. Mirror the
 * shapes in `server/src/routes/invitations.routes.ts`,
 * `server/src/invitations/invitations.service.ts` and
 * `server/src/routes/employees-import.routes.ts` exactly — keep them in
 * lockstep when fields change.
 */

// ── Invitations ─────────────────────────────────────────────────────────

export type InvitationStatus = 'pending' | 'accepted' | 'expired' | 'revoked';

/**
 * Roles an invitation may carry. `visionary` (org creator only) and
 * `auditor_readonly` (dedicated auditor-seat flow) are refused
 * server-side with `role_not_allowed`.
 */
export type InvitableRole = 'integrator' | 'department_lead' | 'employee' | 'quality_lead' | 'qa_personnel';

export interface Invitation {
  id: string;
  organizationId: string;
  email: string;
  role: string;
  departmentId: string | null;
  employeeId: string | null;
  invitedByMembershipId: string | null;
  invitedBy?: { id: string; user: { firstName: string; lastName: string; email: string } } | null;
  expiresAt: string;
  acceptedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
  status: InvitationStatus;
}

export interface InvitationSendResult {
  invitation: Invitation;
  /** Populated only in mock-auth dev mode; null with real Stytch. */
  devToken: string | null;
}

export async function listInvitations(): Promise<Invitation[]> {
  const res = await apiClient.get<{ data: Invitation[] }>('/api/invitations');
  return res.data.data;
}

export interface CreateInvitationRequest {
  email: string;
  /** Defaults to `employee` server-side. */
  role?: InvitableRole;
  departmentId?: string;
  /** Pre-created directory row to link on acceptance. */
  employeeId?: string;
}

export async function createInvitation(body: CreateInvitationRequest): Promise<InvitationSendResult> {
  const res = await apiClient.post<{ data: InvitationSendResult }>('/api/invitations', body);
  return res.data.data;
}

/** Rotate the token + expiry and re-send. Pending or expired invites only. */
export async function resendInvitation(id: string): Promise<InvitationSendResult> {
  const res = await apiClient.post<{ data: InvitationSendResult }>(
    `/api/invitations/${encodeURIComponent(id)}/resend`,
  );
  return res.data.data;
}

export async function revokeInvitation(id: string): Promise<void> {
  await apiClient.delete(`/api/invitations/${encodeURIComponent(id)}`);
}

// ── CSV roster import ───────────────────────────────────────────────────

/** Headers the server expects; only the first three are required per row. */
export const IMPORT_CSV_HEADERS = 'firstName,lastName,email,departmentSlug,jobTitle,role,managerEmail';

export interface ImportEmployeesRequest {
  csv: string;
  /** Send an invitation to each imported row (skips existing members). */
  sendInvites: boolean;
  /** Validate only — no writes; per-row errors come back for preview. */
  dryRun: boolean;
}

export type ImportRowEmployeeOutcome = 'created' | 'exists';
export type ImportRowInviteOutcome = 'invited' | 'already_member' | 'already_invited' | 'failed';

export interface ImportRowResult {
  /** 1-based file line (header is line 1). */
  line: number;
  email: string;
  ok: boolean;
  errors: string[];
  /** Commit only. */
  employee?: ImportRowEmployeeOutcome | null;
  /** Commit + sendInvites only. */
  invite?: ImportRowInviteOutcome | null;
}

export interface ImportSummary {
  total: number;
  // Dry-run:
  valid?: number;
  invalid?: number;
  // Commit:
  created?: number;
  existing?: number;
  invited?: number;
  alreadyMembers?: number;
  alreadyInvited?: number;
  failedInvites?: number;
}

export interface ImportEmployeesResult {
  rows: ImportRowResult[];
  summary: ImportSummary;
}

export async function importEmployees(body: ImportEmployeesRequest): Promise<ImportEmployeesResult> {
  const res = await apiClient.post<{ data: ImportEmployeesResult }>('/api/employees/import', body);
  return res.data.data;
}
